import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { HERO_IMG, PRODUCTS, gbp } from "@/data/shop";
import { useProducts } from "@/lib/products";
import { api, imageUrl } from "@/lib/api";
import { productPath } from "@/lib/seo";

const HERO_ALT = "A colourful mix of bargains including a game controller, headphones, toys, a drill, a toaster and a backpack";
const HERO_DEMOS = true; // demo items join the rotation for now; set to false once there are enough real products
const DEMO_FILES = HERO_DEMOS ? PRODUCTS.map((p) => p.img.split("/").pop()).join(",") : "";

const shuffle = (a) => a.map((x) => [Math.random(), x]).sort((p, q) => p[0] - q[0]).map(([, x]) => x);
const SLIDE_MS = 5000;

// Paid hero ads go first, then every other slide is an ad, so each seller who bought the pack is seen.
const buildSlides = (ads, shop) => {
  if (!ads.length) return shop;
  const out = [...ads];
  shop.forEach((s, i) => out.push(s, ads[i % ads.length]));
  return out;
};

// Slideshow of live ShopaBee products (plus demo items for now) and B Marketplace ads with a Homepage hero boost.
const HeroPicture = () => {
  const { products, loading } = useProducts();
  const [ads, setAds] = useState(null);
  const [heroes, setHeroes] = useState(null);
  const [slides, setSlides] = useState(null);
  const [i, setI] = useState(0);
  const [paused, setPaused] = useState(false);
  const touch = useRef(null);
  const still = typeof window !== "undefined" && window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;
  useEffect(() => {
    api.get("/community/hero").then((r) => setAds(r.data)).catch(() => setAds([]));
    api.get("/hero-images", { params: { demo: DEMO_FILES } }).then((r) => setHeroes(r.data)).catch(() => setHeroes({}));
  }, []);
  useEffect(() => {
    if (loading || ads === null || heroes === null || slides) return;
    const ai = (src) => { const id = src.match(/\/images\/([^/?]+)/)?.[1]; return heroes[id] ? imageUrl(heroes[id]) : heroes[`demo:${id}`] ? imageUrl(heroes[`demo:${id}`]) : null; };
    const shop = shuffle(products.filter((p) => (HERO_DEMOS ? p.demo || p.qty > 0 : !p.demo && p.qty > 0) && p.images?.length && !p.img.startsWith("data:")))
      .slice(0, 8).map((p) => ({ src: p.img, hero: ai(p.img), to: productPath(p), title: p.name, desc: p.desc, price: p.price, condition: p.condition }));
    const paid = shuffle(ads).map((a) => ({ src: imageUrl(a.image), hero: ai(imageUrl(a.image)), to: `/community/ad/${a.id}`, title: a.title, desc: a.description, price: a.price, condition: a.condition, ad: true }));
    setSlides(still ? [paid[0] || shop[0]].filter(Boolean) : buildSlides(paid, shop));
  }, [loading, ads, heroes, products, slides, still]);
  useEffect(() => {
    if (!slides || slides.length < 2 || paused || still) return;
    const t = setInterval(() => setI((n) => (n + 1) % slides.length), SLIDE_MS);
    return () => clearInterval(t);
  }, [slides, paused, still]);
  const cls = "w-full aspect-[3/2] object-cover rounded-3xl border-4 border-white shadow-xl bg-white";
  if (!slides?.length) return <img src={HERO_IMG} width="1536" height="1024" fetchpriority="high" alt={HERO_ALT} data-testid="hero-image" className={cls} />;
  const cur = slides[i];
  const go = (d) => setI((n) => (n + d + slides.length) % slides.length);
  const onTouchStart = (e) => { touch.current = { x: e.touches[0].clientX, moved: false }; setPaused(true); };
  const onTouchEnd = (e) => {
    const dx = e.changedTouches[0].clientX - (touch.current?.x ?? 0);
    if (Math.abs(dx) > 40 && slides.length > 1) { touch.current.moved = true; go(dx < 0 ? 1 : -1); }
    setPaused(false);
  };
  const short = (t) => { const s = (t || "").replace(/\s+/g, " ").trim(); return s.length > 60 ? `${s.slice(0, 57).trimEnd()}…` : s; };
  return (
    <Link to={cur.to} className="block relative" onMouseEnter={() => setPaused(true)} onMouseLeave={() => setPaused(false)} onFocus={() => setPaused(true)} onBlur={() => setPaused(false)}
      onTouchStart={onTouchStart} onTouchEnd={onTouchEnd} onClick={(e) => { if (touch.current?.moved) { e.preventDefault(); touch.current.moved = false; } }}
      aria-label={cur.title} data-testid="hero-image-link">
      <img src={slides[0].src} width="1536" height="1024" alt="" aria-hidden="true" className={`${cls} invisible`} />
      {slides.map((s, n) => (
        <div key={n} aria-hidden={n !== i} className={`absolute inset-0 overflow-hidden rounded-3xl border-4 border-white shadow-xl bg-[#202A35] transition-[opacity,transform] duration-1000 ease-out ${n === i ? "opacity-100 scale-100" : "opacity-0 scale-[1.02]"}`}>
          {s.hero ? <img src={s.hero} width="1536" height="1024" alt={n === i ? s.title : ""} data-testid={n === i ? "hero-image" : undefined} data-ai="1" className="w-full h-full object-cover" />
            : <>
              <img src={s.src} alt="" aria-hidden="true" className="absolute inset-0 w-full h-full object-cover scale-125 blur-2xl opacity-80" />
              <img src={s.src} width="1536" height="1024" alt={n === i ? s.title : ""} data-testid={n === i ? "hero-image" : undefined} className="relative w-full h-full object-contain drop-shadow-xl" />
            </>}
        </div>
      ))}
      {cur.ad && <span className="absolute top-4 right-4 text-xs font-bold px-3 py-1 rounded-full bg-white text-[#202A35] shadow" data-testid="hero-ad-label">B Marketplace ad · {cur.title}</span>}
      <div className="absolute left-3 right-3 bottom-8 sm:left-4 sm:right-auto sm:max-w-[70%] rounded-xl bg-[#202A35]/75 backdrop-blur-sm text-white px-3 py-1.5 leading-tight" data-testid="hero-caption">
        <p className="text-[11px] sm:text-xs font-bold truncate" data-testid="hero-caption-title">{cur.title}</p>
        {cur.desc && <p className="text-[10px] sm:text-[11px] text-white/80 truncate" data-testid="hero-caption-desc">{short(cur.desc)}</p>}
        <p className="text-[10px] sm:text-[11px] mt-0.5"><b data-testid="hero-caption-price">{gbp(cur.price)}</b>{cur.condition && <> · {cur.condition}</>}{cur.ad && <> · B Marketplace ad</>}</p>
      </div>
      {slides.length > 1 && !still && (
        <span className="absolute bottom-4 left-5 flex gap-1.5" data-testid="hero-dots">
          {slides.map((_, n) => <span key={n} className={`h-1.5 rounded-full transition-[width,background-color] ${n === i ? "w-5 bg-white" : "w-1.5 bg-white/60"}`} />)}
        </span>
      )}
    </Link>
  );
};

// Real lowest price among live ShopaBee products; no badge when nothing is live.
const PriceBadge = () => {
  const { products, loading } = useProducts();
  const live = products.filter((p) => !p.demo && p.qty > 0).map((p) => p.price);
  if (loading || !live.length) return null;
  return (
    <div className="sticker absolute -top-4 -left-3 sm:-left-5 bg-[#FF6B5B] text-white rounded-full w-24 h-24 flex flex-col items-center justify-center -rotate-12 shadow-lg border-4 border-white" data-testid="hero-badge">
      <span className="text-[10px] font-bold leading-tight text-center">Bargains<br />from just</span>
      <span className="font-display text-2xl font-bold leading-tight" data-testid="hero-badge-price">{gbp(Math.min(...live))}</span>
    </div>
  );
};

export const Hero = () => (
  <section className="bg-[#FFD23F] relative overflow-hidden" data-testid="hero-section">
    <div className="absolute -top-16 -right-16 w-64 h-64 rounded-full bg-[#FFE27A]" aria-hidden="true" />
    <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10 lg:py-14 grid lg:grid-cols-12 gap-10 items-center">
      <div className="lg:col-span-5 fade-up">
        <div className="flex flex-wrap gap-2 mb-5">
          {[["New", "#D9F5E8"], ["Used", "#FFE5D1"], ["Open Box", "#DCEEFF"], ["Clearance", "#FFE0EC"]].map(([t, c]) => (
            <span key={t} className="text-xs font-bold px-3 py-1 rounded-full text-[#202A35] border-2 border-[#202A35]" style={{ background: c }}>{t}</span>
          ))}
        </div>
        <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-bold text-[#202A35] leading-[1.02]" data-testid="hero-headline">
          You Never Know What You'll Find!
        </h1>
        <p className="mt-4 font-display text-lg sm:text-xl font-semibold text-[#202A35]" data-testid="hero-supporting-phrase">
          New stuff. Used stuff. All sorts of stuff!
        </p>
        <p className="mt-3 text-base text-[#202A35]/85 leading-relaxed max-w-md" data-testid="hero-text">
          New, used, open-box and everything in between. Have a browse, find something brilliant and grab yourself a bargain.
        </p>
        <div className="mt-7 flex flex-wrap gap-3">
          <Link to="/shop" data-testid="hero-explore-btn"
            className="group inline-flex items-center gap-2 h-12 px-6 rounded-full bg-[#202A35] text-white text-sm font-bold shadow-[0_4px_0_#000] hover:translate-y-0.5 hover:shadow-[0_2px_0_#000] transition-[transform,box-shadow]">
            Grab a Bargain! <ArrowRight size={16} className="transition-transform group-hover:translate-x-1" />
          </Link>
          <Link to="/shop?sort=newest" data-testid="hero-new-btn"
            className="inline-flex items-center h-12 px-6 rounded-full bg-white border-2 border-[#202A35] text-[#202A35] text-sm font-bold hover:bg-[#FFF9E8] transition-colors">
            See What's New
          </Link>
        </div>
      </div>
      <div className="lg:col-span-7 fade-up" style={{ animationDelay: "120ms" }}>
        <div className="relative lg:rotate-1">
          <HeroPicture />
          <PriceBadge />
          <div className="absolute -bottom-4 right-4 bg-white rounded-full shadow-lg px-4 py-2 text-sm font-bold text-[#202A35] rotate-2" data-testid="hero-condition-note">
            Every condition clearly described
          </div>
        </div>
      </div>
    </div>
  </section>
);
